import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldHalf,
  ShieldCheck,
  Gauge,
  AlertTriangle,
  Activity,
  Fingerprint,
  Flame,
  UserCheck,
  Bot,
  Server,
  Network,
  Wrench,
  RotateCw,
  User,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { cn } from "../../../lib/utils";
import { GatewayCisoApi } from "../Api/gatewayApi";
import { apiError } from "../utils/gatewayUtils";
import { useToast } from "../../../../hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import "../AgenticGateway.css";

// ═══════════════════════════ constants ═══════════════════════════

const WINDOWS: [string, string][] = [["24h", "24h"], ["7d", "7 days"], ["30d", "30 days"], ["90d", "90 days"]];

// Severity / risk band → badge class + a raw colour (SVG bars can't read Tailwind tokens).
const SEV: Record<string, { fg: string; badge: string }> = {
  CRITICAL: { fg: "#f87171", badge: "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30" },
  HIGH: { fg: "#fbbf24", badge: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30" },
  MEDIUM: { fg: "#facc15", badge: "bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 border-yellow-500/30" },
  LOW: { fg: "#34d399", badge: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30" },
};
const sevOf = (s?: string) => SEV[(s || "LOW").toUpperCase()] || SEV.LOW;

// KPI tiles where an increase is a BAD signal (coloured red) vs a good one (green).
const UP_IS_BAD = new Set(["sensitive", "denied", "gaps", "risky"]);

const SENS_COL: Record<string, string> = { PUBLIC: "#94a3b8", INTERNAL: "#60a5fa", CONFIDENTIAL: "#fbbf24", RESTRICTED: "#f87171" };
const friendlySens = (s: string) => (({ PUBLIC: "Public", INTERNAL: "Internal", CONFIDENTIAL: "Confidential", RESTRICTED: "Restricted" } as Record<string, string>)[s] || s);
const DCLASS_CLS: Record<string, string> = { Restricted: "text-red-600 dark:text-red-400", Confidential: "text-amber-600 dark:text-amber-400", Internal: "text-blue-600 dark:text-blue-400", Public: "text-muted-foreground" };

// ═══════════════════════════ helpers ═══════════════════════════

const fmtNum = (v: any) => (v == null ? "—" : Number(v).toLocaleString());
const fmtSeen = (t: any) => (t ? String(t).replace("T", " ").slice(0, 16) : "");

// "2026-08-19T14:00" → 14:00 ; day buckets → MM-DD
function fmtBucket(t: any): string {
  if (!t) return "";
  const str = String(t);
  const m = /T(\d{2}):/.exec(str);
  if (m) return m[1] + ":00";
  const d = /(\d{2})-(\d{2})$/.exec(str.slice(0, 10));
  return d ? `${d[1]}-${d[2]}` : str.slice(5, 10);
}

// ═══════════════════════════ small building blocks ═══════════════════════════

function Card({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={cn("ag-card p-4", className)}>{children}</div>;
}

function CardHead({ icon, title, right, iconClass }: { icon: React.ReactNode; title: React.ReactNode; right?: React.ReactNode; iconClass?: string }) {
  return (
    <div className="flex items-center gap-2 mb-3.5 text-sm font-semibold text-foreground">
      <span className={cn("text-primary", iconClass)}>{icon}</span>
      <span>{title}</span>
      {right && <span className="ml-auto font-normal">{right}</span>}
    </div>
  );
}

function SectionLoading({ text }: { text: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
      <Loader2 className="w-4 h-4 animate-spin" /> {text}
    </div>
  );
}
function SectionError({ what, err }: { what: string; err: string }) {
  return <div className="py-6 text-center text-sm text-red-600 dark:text-red-400">Could not load {what} — {err}</div>;
}
function SectionEmpty({ children }: { children: React.ReactNode }) {
  return <div className="py-6 text-center text-sm text-muted-foreground">{children}</div>;
}

function SevBadge({ value }: { value?: string }) {
  const s = sevOf(value);
  return <span className={cn("inline-flex items-center text-[10.5px] font-bold px-2 py-0.5 rounded-md border tracking-wide whitespace-nowrap", s.badge)}>{value || "LOW"}</span>;
}

// ── posture gauge (single-value ring) ────────────────────────────────
function PostureDonut({ score, col }: { score: number; col: string }) {
  const r = 52, circ = 2 * Math.PI * r, arc = (Math.max(0, Math.min(100, score)) / 100) * circ;
  return (
    <svg viewBox="0 0 120 120" className="w-[132px] h-[132px]" role="img" aria-label={`Posture ${score} of 100`}>
      <circle cx="60" cy="60" r={r} fill="none" strokeWidth={12} style={{ stroke: "hsl(var(--border))" }} />
      <circle cx="60" cy="60" r={r} fill="none" stroke={col} strokeWidth={12} strokeLinecap="round" strokeDasharray={`${arc.toFixed(1)} ${circ.toFixed(1)}`} transform="rotate(-90 60 60)" />
      <text x="60" y="56" textAnchor="middle" style={{ fill: "hsl(var(--foreground))", fontSize: 30, fontWeight: 700 }}>{score}</text>
      <text x="60" y="74" textAnchor="middle" style={{ fill: "hsl(var(--muted-foreground))", fontSize: 10, letterSpacing: 1.5 }}>POSTURE</text>
    </svg>
  );
}

// ── sensitivity donut (multi-segment) ────────────────────────────────
function SensitivityDonut({ mix, total }: { mix: any[]; total: number }) {
  const r = 52, circ = 2 * Math.PI * r;
  let off = 0;
  const segs = (mix || []).filter((s) => s.count > 0).map((s, i) => {
    const len = (total > 0 ? s.count / total : 0) * circ;
    const seg = (
      <circle key={i} cx="60" cy="60" r={r} fill="none" stroke={SENS_COL[s.sensitivity] || "#94a3b8"} strokeWidth={14}
        strokeDasharray={`${len.toFixed(2)} ${(circ - len).toFixed(2)}`} strokeDashoffset={(-off).toFixed(2)} transform="rotate(-90 60 60)" />
    );
    off += len;
    return seg;
  });
  return (
    <svg viewBox="0 0 120 120" className="w-[132px] h-[132px]">
      <circle cx="60" cy="60" r={r} fill="none" strokeWidth={14} style={{ stroke: "hsl(var(--border))" }} />
      {segs}
      <text x="60" y="57" textAnchor="middle" style={{ fill: "hsl(var(--foreground))", fontSize: 24, fontWeight: 700 }}>{fmtNum(total)}</text>
      <text x="60" y="74" textAnchor="middle" style={{ fill: "hsl(var(--muted-foreground))", fontSize: 10, letterSpacing: 1.5 }}>CLASSIFIED</text>
    </svg>
  );
}

// ── coverage / hotspot progress bar ──────────────────────────────────
function CovRow({ label, val, pct }: { label: string; val: string; pct: number }) {
  const col = pct >= 90 ? "#34d399" : pct >= 60 ? "#fbbf24" : "#f87171";
  return (
    <div className="mb-3.5">
      <div className="flex justify-between text-[12.5px] mb-1.5">
        <span className="text-foreground/85">{label}</span>
        <span className="font-bold tabular-nums text-foreground">{val}</span>
      </div>
      <div className="h-2 rounded-full bg-primary/10 overflow-hidden">
        <div className="h-full rounded-full transition-[width]" style={{ width: `${Math.max(0, Math.min(100, pct))}%`, background: col }} />
      </div>
    </div>
  );
}

// ═══════════════════════════ main ═══════════════════════════

export default function GatewayCisoDashboard() {
  const { toast } = useToast();
  const navigate = useNavigate();

  const [win, setWin] = useState("24h");
  const [priorityShown, setPriorityShown] = useState(6);
  const [princTab, setPrincTab] = useState<"humans" | "nhis">("humans");

  // Each widget loads independently (a slow section never blocks the rest) — null = still loading.
  const [overview, setOverview] = useState<any>(null);
  const [overviewErr, setOverviewErr] = useState<string | null>(null);
  const [actions, setActions] = useState<any[] | null>(null);
  const [actionsErr, setActionsErr] = useState<string | null>(null);
  const [traffic, setTraffic] = useState<any>(null);
  const [trafficErr, setTrafficErr] = useState<string | null>(null);
  const [hotspots, setHotspots] = useState<any>(null);
  const [hotspotsErr, setHotspotsErr] = useState<string | null>(null);
  const [chains, setChains] = useState<any[] | null>(null);
  const [chainsErr, setChainsErr] = useState<string | null>(null);
  const [topTools, setTopTools] = useState<any[] | null>(null);
  const [topToolsErr, setTopToolsErr] = useState<string | null>(null);

  const load = useCallback((window: string) => {
    setPriorityShown(6);
    setOverview(null); setOverviewErr(null);
    setActions(null); setActionsErr(null);
    setTraffic(null); setTrafficErr(null);
    setHotspots(null); setHotspotsErr(null);
    setChains(null); setChainsErr(null);
    setTopTools(null); setTopToolsErr(null);

    GatewayCisoApi.dashboard.overview(window).then((o) => setOverview(o || {})).catch((e) => setOverviewErr(apiError(e)));
    GatewayCisoApi.dashboard.priorityActions().then((a) => setActions(Array.isArray(a) ? a : [])).catch((e) => setActionsErr(apiError(e)));
    GatewayCisoApi.dashboard.traffic(window).then((t) => setTraffic(t || {})).catch((e) => setTrafficErr(apiError(e)));
    GatewayCisoApi.dashboard.hotspots().then((h) => setHotspots(h || {})).catch((e) => setHotspotsErr(apiError(e)));
    GatewayCisoApi.dashboard.chains().then((c) => setChains((c && c.chains) || [])).catch((e) => setChainsErr(apiError(e)));
    GatewayCisoApi.dashboard.topTools().then((t) => setTopTools((t && t.tools) || [])).catch((e) => setTopToolsErr(apiError(e)));
  }, []);

  useEffect(() => { load(win); }, [win, load]);

  // Priority-action button → route by kind; CREATE_POLICY hands the brief to the policy assistant via ?seed=.
  const act = (a: any) => {
    if (!a) return;
    const kind = a.actionKind;
    if (kind === "CREATE_POLICY") {
      const seed = (a.context && a.context.assistantSeed) || a.detail || "";
      navigate(`/agentic-gateway/policies?seed=${encodeURIComponent(seed)}`);
      toast({ title: "Policy assistant seeded — review, then Generate" });
    } else if (kind === "REVIEW_CHAIN") {
      navigate("/agentic-gateway/identity-graph");
      toast({ title: "Opening the identity graph to review this reach" });
    } else {
      navigate("/agentic-gateway/audit");
      toast({ title: "Opening the audit trail to investigate" });
    }
  };

  // ── derived overview slices ──
  const kpis: any[] = overview?.kpis || [];
  const posture = overview?.posture || {};
  const coverage = overview?.coverage || {};
  const sensMix: any[] = overview?.sensitivityMix || [];
  const classifiedTotal = overview?.classifiedTotal || 0;

  const trafficPts = (traffic?.points || []).map((p: any) => ({
    label: fmtBucket(p.t),
    req: (p.allowed || 0) + (p.denied || 0),
    sensitive: p.sensitive || 0,
    denied: p.denied || 0,
    classified: p.requests || 0,
    mcp: p.mcp || 0,
    a2a: p.a2a || 0,
  }));
  const tot = trafficPts.reduce(
    (a: any, p: any) => ({
      requests: a.requests + p.req, classified: a.classified + p.classified, sensitive: a.sensitive + p.sensitive,
      mcp: a.mcp + p.mcp, a2a: a.a2a + p.a2a, denied: a.denied + p.denied,
    }),
    { requests: 0, classified: 0, sensitive: 0, mcp: 0, a2a: 0, denied: 0 }
  );

  const postScore = posture.score == null ? 0 : posture.score;
  const postCol = postScore >= 80 ? "#34d399" : postScore >= 60 ? "#3fb6a8" : postScore >= 40 ? "#fbbf24" : "#f87171";
  const restrictedPct = (sensMix.find((s) => s.sensitivity === "RESTRICTED") || {}).pct || 0;

  const shownActions = (actions || []).slice(0, priorityShown);
  const remaining = (actions || []).length - shownActions.length;

  const principals = princTab === "nhis" ? hotspots?.nhis || [] : hotspots?.humans || [];

  return (
    <div className="space-y-4">
      {/* header — window picker + refresh */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5 text-[22px] font-bold text-foreground">
          <ShieldHalf className="w-5 h-5 text-primary" /> Governance Posture
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center gap-0.5 rounded-lg border border-border ag-surface p-1">
            {WINDOWS.map(([id, label]) => (
              <button key={id} onClick={() => setWin(id)}
                className={cn("px-3 py-1.5 rounded-md text-xs font-semibold transition-colors", id === win ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground")}>
                {label}
              </button>
            ))}
          </div>
          <button onClick={() => load(win)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-border bg-card text-foreground hover:border-primary/40 transition-colors">
            <RotateCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {overviewErr ? (
          <div className="col-span-full"><Card><SectionError what="KPIs" err={overviewErr} /></Card></div>
        ) : !overview ? (
          Array.from({ length: 6 }).map((_, i) => <div key={i} className="ag-card min-h-[92px] animate-pulse" />)
        ) : (
          kpis.map((k, i) => {
            let delta: React.ReactNode = <span className="text-muted-foreground">{k.hint || ""}</span>;
            if (k.previous != null) {
              const prev = Number(k.previous), val = Number(k.value);
              if (prev === 0 && val === 0) delta = <span className="text-muted-foreground">no change</span>;
              else {
                const pct = prev === 0 ? 100 : Math.round(((val - prev) / prev) * 100);
                const up = val >= prev;
                const bad = UP_IS_BAD.has(k.id) ? up : !up;
                const cls = pct === 0 ? "text-muted-foreground" : bad ? "text-red-600 dark:text-red-400" : "text-emerald-600 dark:text-emerald-400";
                delta = <><span className={cn("font-bold", cls)}>{pct === 0 ? "" : up ? "▲" : "▼"} {Math.abs(pct)}%</span> <span className="text-muted-foreground">vs prev {win}</span></>;
              }
            }
            return (
              <div key={i} className="ag-card px-4 py-3.5 min-h-[92px]">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{k.label}</div>
                <div className="text-[28px] font-bold text-foreground leading-tight tabular-nums mt-1">{fmtNum(k.value)}</div>
                <div className="text-xs mt-1">{delta}</div>
              </div>
            );
          })
        )}
      </div>

      {/* posture + priority actions */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-4">
        <Card>
          <CardHead icon={<Gauge className="w-4 h-4" />} title="Executive posture"
            right={<span className="text-muted-foreground text-xs cursor-help" title="Posture score (0–100), NOT a risk score. Higher is better — how tightly your estate is governed right now.">ⓘ</span>} />
          {overviewErr ? <SectionError what="posture" err={overviewErr} /> : !overview ? <SectionLoading text="Scoring posture…" /> : (
            <div className="flex gap-4 items-center flex-wrap">
              <div className="text-center shrink-0">
                <PostureDonut score={postScore} col={postCol} />
                <div className="text-[11px] text-muted-foreground mt-1.5">Grade {posture.grade || "—"} · higher = tighter</div>
              </div>
              <div className="flex-1 min-w-[200px]">
                <div className="text-[13px] font-semibold text-foreground mb-1">{posture.headline || ""}</div>
                <div className="text-xs text-muted-foreground mb-3">
                  <b className="text-foreground">{posture.critical || 0}</b> critical · <b className="text-foreground">{posture.warning || 0}</b> warning · <b className="text-foreground">{posture.coveredTrafficPct || 0}%</b> decided by policy
                </div>
                <div className="flex flex-col gap-2.5">
                  {(posture.lines || []).length ? (posture.lines || []).map((l: any, i: number) => {
                    const c = ({ GOOD: "#34d399", WARN: "#fbbf24", CRITICAL: "#f87171" } as Record<string, string>)[l.status] || "#94a3b8";
                    return (
                      <div key={i} className="flex gap-2 items-start">
                        <span className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: c }} />
                        <div>
                          <div className="text-[12.5px] font-semibold text-foreground">{l.title}</div>
                          <div className="text-[11.5px] text-muted-foreground">{l.why || l.detail || ""}</div>
                        </div>
                      </div>
                    );
                  }) : <span className="text-muted-foreground text-xs">No scored checks.</span>}
                </div>
              </div>
            </div>
          )}
        </Card>

        <Card>
          <CardHead icon={<AlertTriangle className="w-4 h-4" />} title="Priority actions"
            right={<span className="text-muted-foreground text-xs">{(actions || []).length} open · ranked by blast radius &amp; sensitivity</span>} />
          {actionsErr ? <SectionError what="priority actions" err={actionsErr} /> : !actions ? <SectionLoading text="Ranking priority actions…" /> : actions.length === 0 ? (
            <SectionEmpty><span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /> Nothing needs attention in this window.</span></SectionEmpty>
          ) : (
            <>
              <div className="flex flex-col gap-2.5 max-h-[60vh] overflow-auto">
                {shownActions.map((a, i) => {
                  const s = sevOf(a.severity);
                  return (
                    <div key={i} className="rounded-lg border border-border border-l-[3px] bg-foreground/[0.015] px-3 py-2.5 portal-card-hover" style={{ borderLeftColor: s.fg }}>
                      <div className="flex items-center gap-2.5">
                        <SevBadge value={a.severity} />
                        <span className="font-semibold text-[13px] text-foreground flex-1 min-w-0">{a.title}</span>
                        <button onClick={() => act(a)}
                          className="text-xs font-semibold px-2.5 py-1 rounded-md border border-primary/40 text-primary hover:bg-primary hover:text-white transition-colors whitespace-nowrap">
                          {a.actionLabel || "Review"}
                        </button>
                      </div>
                      <div className="text-[12.5px] text-muted-foreground my-2 leading-relaxed">{a.detail}</div>
                      <div className="flex items-center gap-2 flex-wrap text-[11.5px] text-muted-foreground">
                        {(a.entities || []).map((e: string, j: number) => <span key={j} className="ag-surface rounded px-1.5 py-0.5">{e}</span>)}
                        {a.owner && <span className="ml-auto font-semibold text-foreground/80">Owner: {a.owner}</span>}
                        {a.lastSeen && <span className="opacity-70">{fmtSeen(a.lastSeen)}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
              {remaining > 0 && (
                <button onClick={() => setPriorityShown((n) => n + 6)}
                  className="w-full mt-2.5 text-xs font-semibold text-muted-foreground border border-dashed border-border rounded-lg py-2 hover:text-foreground hover:border-primary/40 transition-colors">
                  Load {Math.min(6, remaining)} more · {remaining} remaining
                </button>
              )}
            </>
          )}
        </Card>
      </div>

      {/* traffic + sensitivity */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.7fr_1fr] gap-4">
        <Card>
          <CardHead icon={<Activity className="w-4 h-4" />} title="Gateway traffic & intervention"
            right={<span className="text-muted-foreground text-[11px]">live · {win}</span>} />
          {trafficErr ? <SectionError what="traffic" err={trafficErr} /> : !traffic ? <SectionLoading text="Loading traffic…" /> : trafficPts.length === 0 ? (
            <SectionEmpty>No traffic in this window.</SectionEmpty>
          ) : (
            <>
              <div className="h-[200px] -ml-2">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={trafficPts} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="cdReq" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#6aa3ff" stopOpacity={0.28} />
                        <stop offset="100%" stopColor="#6aa3ff" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="hsl(var(--border))" strokeOpacity={0.5} vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} tickLine={false} axisLine={false} minTickGap={28} />
                    <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} tickLine={false} axisLine={false} width={34} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "hsl(var(--foreground))" }} itemStyle={{ padding: 0 }} />
                    <Area type="monotone" dataKey="req" name="Requests" stroke="#6aa3ff" strokeWidth={2} fill="url(#cdReq)" />
                    <Line type="monotone" dataKey="sensitive" name="Sensitive" stroke="#f87171" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="denied" name="Denied" stroke="#34d399" strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div className="flex gap-4 justify-center mt-2 text-[11.5px] text-muted-foreground">
                <span className="inline-flex items-center gap-1.5"><i className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: "#6aa3ff" }} />Requests</span>
                <span className="inline-flex items-center gap-1.5"><i className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: "#f87171" }} />Sensitive</span>
                <span className="inline-flex items-center gap-1.5"><i className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: "#34d399" }} />Denied</span>
              </div>
              <div className="grid grid-cols-6 gap-1.5 mt-3">
                {[["Requests", tot.requests], ["Classified", tot.classified], ["Sensitive", tot.sensitive], ["MCP", tot.mcp], ["A2A", tot.a2a], ["Denied", tot.denied]].map(([l, v]) => (
                  <div key={l as string} className="text-center rounded-lg ag-surface py-1.5 px-1">
                    <div className="text-[15px] font-bold tabular-nums text-foreground">{fmtNum(v)}</div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-0.5">{l}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>

        <Card>
          <CardHead icon={<Fingerprint className="w-4 h-4" />} title="Data sensitivity" />
          {overviewErr ? <SectionError what="sensitivity" err={overviewErr} /> : !overview ? <SectionLoading text="Classifying…" /> : (
            <>
              <div className="flex gap-4 items-center flex-wrap">
                <SensitivityDonut mix={sensMix} total={classifiedTotal} />
                <div className="flex-1 min-w-[130px] flex flex-col gap-2">
                  {sensMix.map((s, i) => (
                    <div key={i} className="flex items-center gap-2 text-[12.5px]">
                      <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: SENS_COL[s.sensitivity] || "#94a3b8" }} />
                      <span className="flex-1 text-foreground/85">{friendlySens(s.sensitivity)}</span>
                      <span className="font-bold tabular-nums text-foreground">{s.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
              {(restrictedPct > 0 || coverage.enforcementGaps > 0) && (
                <div className="mt-3 text-[11.5px] text-amber-600 dark:text-amber-400 bg-amber-500/[0.08] rounded-lg px-2.5 py-2">
                  {restrictedPct}% of classified data is Restricted · <b>{coverage.enforcementGaps || 0}</b> sensitive capabilities have no enforcement policy.
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {/* risk hotspots */}
      <div>
        <div className="flex items-center gap-2.5 text-[15px] font-semibold text-foreground my-1.5">
          <Flame className="w-4 h-4 text-red-600 dark:text-red-400" /> Risk hotspots
          <span className="font-normal text-xs text-muted-foreground">highest combined sensitivity, privilege &amp; behavioural risk</span>
        </div>
        {hotspotsErr ? <Card><SectionError what="risk hotspots" err={hotspotsErr} /></Card> : !hotspots ? <Card><SectionLoading text="Ranking risk hotspots…" /></Card> : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card>
              <div className="flex items-center gap-2 mb-3.5 text-sm font-semibold text-foreground">
                <UserCheck className="w-4 h-4 text-primary" /> Risky principals
                <span className="ml-auto inline-flex gap-0.5 p-0.5 rounded-md ag-surface">
                  {(["humans", "nhis"] as const).map((t) => (
                    <button key={t} onClick={() => setPrincTab(t)}
                      className={cn("text-[11px] font-semibold px-2 py-0.5 rounded", princTab === t ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground")}>
                      {t === "humans" ? "Humans" : "NHIs"}
                    </button>
                  ))}
                </span>
              </div>
              <HotspotRows list={principals} />
            </Card>
            <Card>
              <CardHead icon={<Bot className="w-4 h-4" />} title="Risky agents" />
              <HotspotRows list={hotspots.agents} />
            </Card>
            <Card>
              <CardHead icon={<Server className="w-4 h-4" />} title="Risky MCP servers" />
              <HotspotRows list={hotspots.servers} />
            </Card>
          </div>
        )}
      </div>

      {/* chain visibility */}
      <Card>
        <CardHead icon={<Network className="w-4 h-4" />} title="Human → agent → MCP chain visibility"
          right={<span className="text-muted-foreground text-xs">the gateway is the shared observation point for every request path</span>} />
        {chainsErr ? <SectionError what="chain visibility" err={chainsErr} /> : !chains ? <SectionLoading text="Reconstructing chains…" /> : chains.length === 0 ? (
          <SectionEmpty>No governed traces in view.</SectionEmpty>
        ) : (
          <div className="overflow-x-auto">
            <Table className="[&_td]:px-2.5 [&_td]:py-2 [&_th]:px-2.5 [&_th]:h-9">
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  {["Human", "Agent", "Agent-to-agent", "MCP server", "Tool / path", "Risk", "Seen"].map((h) => (
                    <TableHead key={h} className="text-[10.5px] uppercase tracking-wider text-muted-foreground font-semibold whitespace-nowrap">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {chains.map((r, i) => (
                  <TableRow key={i} className="border-border hover:bg-primary/[0.025]">
                    <TableCell className="text-[12.5px] text-foreground/90 whitespace-nowrap">
                      {(r.humanKind || "HUMAN") === "NHI" ? <Bot className="inline w-3.5 h-3.5 text-muted-foreground mr-1.5" /> : <User className="inline w-3.5 h-3.5 text-muted-foreground mr-1.5" />}
                      {r.human || "—"}
                    </TableCell>
                    <TableCell className="text-[12.5px] text-foreground/90 whitespace-nowrap"><Bot className="inline w-3.5 h-3.5 text-muted-foreground mr-1.5" />{r.agent || "—"}</TableCell>
                    <TableCell className="text-[12.5px] text-muted-foreground whitespace-nowrap">{r.agentToAgent ? `→ ${r.agentToAgent}` : "—"}</TableCell>
                    <TableCell className="text-[12.5px] text-foreground/90 whitespace-nowrap"><Server className="inline w-3.5 h-3.5 text-muted-foreground mr-1.5" />{r.server || "—"}</TableCell>
                    <TableCell>
                      <div className="text-[12.5px] font-semibold text-foreground">{r.tool || "—"}</div>
                      <div className="text-[10.5px] text-muted-foreground">{(r.protocol || "") + (r.capabilityType ? ` · ${String(r.capabilityType).toLowerCase()}` : "")}</div>
                    </TableCell>
                    <TableCell><SevBadge value={r.risk} /></TableCell>
                    <TableCell className="text-[12.5px] text-muted-foreground whitespace-nowrap">{fmtSeen(r.seen)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* top tools + coverage */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.7fr_1fr] gap-4">
        <Card>
          <CardHead icon={<Wrench className="w-4 h-4" />} title="Top MCP tools by call volume" />
          {topToolsErr ? <SectionError what="top tools" err={topToolsErr} /> : !topTools ? <SectionLoading text="Ranking tools…" /> : topTools.length === 0 ? (
            <SectionEmpty>No tool activity yet.</SectionEmpty>
          ) : (
            <div className="overflow-x-auto">
              <Table className="[&_td]:px-2.5 [&_td]:py-2 [&_th]:px-2.5 [&_th]:h-9">
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    {["Tool", "Calls", "Data class", "Denied", "Risk"].map((h) => (
                      <TableHead key={h} className="text-[10.5px] uppercase tracking-wider text-muted-foreground font-semibold whitespace-nowrap">{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topTools.map((t, i) => (
                    <TableRow key={i} className="border-border hover:bg-primary/[0.025]">
                      <TableCell>
                        <div className="text-[12.5px] font-semibold text-foreground">{t.tool}</div>
                        <div className="text-[10.5px] text-muted-foreground">{t.server || ""}</div>
                      </TableCell>
                      <TableCell className="text-[12.5px] text-right tabular-nums font-semibold text-foreground">{fmtNum(t.calls)}</TableCell>
                      <TableCell className={cn("text-[12.5px] font-semibold", DCLASS_CLS[t.dataClass] || "text-muted-foreground")}>{t.dataClass}</TableCell>
                      <TableCell className="text-[12.5px] text-right tabular-nums">{t.denied ? <span className="text-red-600 dark:text-red-400 font-semibold">{fmtNum(t.denied)}</span> : "0"}</TableCell>
                      <TableCell><SevBadge value={t.risk} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>

        <Card>
          <CardHead icon={<ShieldCheck className="w-4 h-4" />} title="Policy coverage" />
          {overviewErr ? <SectionError what="coverage" err={overviewErr} /> : !overview ? <SectionLoading text="Measuring coverage…" /> : (
            <>
              <CovRow label="MCP servers with policies" val={`${coverage.serversWithPolicies || 0} / ${coverage.registeredServers || 0}`} pct={coverage.registeredServers > 0 ? Math.round((100 * (coverage.serversWithPolicies || 0)) / coverage.registeredServers) : 0} />
              <CovRow label="MCP capabilities with a governing policy" val={`${coverage.mcpCapabilitiesGoverned || 0} / ${coverage.mcpCapabilitiesTotal || 0}`} pct={coverage.mcpCapabilitiesTotal > 0 ? Math.round((100 * (coverage.mcpCapabilitiesGoverned || 0)) / coverage.mcpCapabilitiesTotal) : 0} />
              <CovRow label="A2A skills with a governing policy" val={`${coverage.skillsGoverned || 0} / ${coverage.skillsTotal || 0}`} pct={coverage.skillsTotal > 0 ? Math.round((100 * (coverage.skillsGoverned || 0)) / coverage.skillsTotal) : 0} />
              <CovRow label="Agents with a governing policy" val={`${coverage.agentsGoverned || 0} / ${coverage.agentsTotal || 0}`} pct={coverage.agentsTotal > 0 ? Math.round((100 * (coverage.agentsGoverned || 0)) / coverage.agentsTotal) : 0} />
              <CovRow label="Requests decided by an explicit policy" val={`${coverage.decisionsAttributedPct || 0}%`} pct={coverage.decisionsAttributedPct || 0} />
              {coverage.egressGaps > 0 && (
                <div className="mt-1 rounded-lg border border-red-500/40 bg-red-500/[0.09] px-3 py-2.5 text-xs text-red-300 leading-relaxed">
                  <b>{coverage.egressGaps}</b> data-egress gap{coverage.egressGaps === 1 ? "" : "s"} — sensitive capabilities are classified, but no egress rule redacts or blocks them.
                  <button onClick={() => navigate("/agentic-gateway/policies")}
                    className="mt-2 block text-xs font-semibold px-2.5 py-1 rounded-md border border-primary/40 text-primary hover:bg-primary hover:text-white transition-colors">
                    Configure controls →
                  </button>
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      <div className="text-center text-[11px] text-muted-foreground pt-2">
        Gateway is the system of record for governed agentic traffic &amp; policy decisions.
      </div>
    </div>
  );
}

// Ranked-list rows shared by the three hotspot cards (principals / agents / servers).
function HotspotRows({ list }: { list?: any[] }) {
  if (!list || !list.length) return <div className="py-3.5 text-center text-sm text-muted-foreground">None observed.</div>;
  return (
    <div className="flex flex-col gap-3">
      {list.map((x, i) => {
        const s = sevOf(x.band);
        return (
          <div key={i}>
            <div className="flex justify-between items-baseline gap-2">
              <span className="text-[13px] font-semibold text-foreground truncate">{x.name}</span>
              <span className="text-[15px] font-bold tabular-nums shrink-0" style={{ color: s.fg }}>{x.score}</span>
            </div>
            <div className="h-[5px] rounded-full bg-primary/10 overflow-hidden my-1">
              <div className="h-full rounded-full" style={{ width: `${Math.max(3, Math.min(100, x.score))}%`, background: s.fg }} />
            </div>
            <div className="text-[11.5px] text-muted-foreground">{x.reason || ""}</div>
            {(x.facts || []).length > 0 && (
              <div className="flex gap-1 flex-wrap mt-1">
                {(x.facts || []).map((f: string, j: number) => <span key={j} className="text-[10.5px] ag-surface rounded px-1.5 py-0.5 text-muted-foreground">{f}</span>)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
